import type {
  AffiliationValueType,
  EventCategoryValueType,
  CountryValueType,
  CurrencyValueType,
  LanguageValueType,
  EventLocationValueType,
} from '@susu/custom-made/Avo';
import Avo from '@susu/custom-made/Avo';

import type { State } from '../providers';
import type { ClientType } from './constants';
import {
  Clients,
  hiddenValuesStorageName,
  MeasurementsCoatProductType,
  stateExpiryStorageName,
  stateStorageName,
  ConfiguratorOrigin,
} from './constants';
import { imageDefinition } from './imageDefinition';
import { imageUrlBuilder } from './imageUrlProvider/imageUrlProvider';
import type {
  Fabric,
  FabricFilter,
  FabricGroup,
  HiddenOptionsAndValues,
  Option,
  OptionsSelection,
  typeFabricList,
  StylePreset,
} from './interfaces';
import { getConfigurationTypeBySection, getSectionByConfigurationType, Section } from './navigation';
import type { ProductType, ProductTypeMeas } from './productDefinition';
import {
  additionalConfigurationTypeByProduct,
  ConfigurationTypes,
  MeasProductTypes,
  Products,
  rotationConfigurationTypes,
} from './productDefinition';

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
    analytics: any;
    DD_RUM: any;
    DD_LOGS: any;
  }
}

export const isFalsy = (value: number | boolean | undefined | null | string) => {
  // I know 'undefined' is not falsy but we need to catch it too
  const falsies = [false, 'false', 0, -0, '0', '', null, undefined, NaN, 'undefined'];

  return falsies.some(falsy => value === falsy);
};

export const clone = async <T>(source: T): Promise<T> => {
  return JSON.parse(JSON.stringify(source));
};

export const isObjectEmpty = (objectToCheck: Object) => {
  return objectToCheck && Object.keys(objectToCheck).length === 0 && objectToCheck.constructor === Object;
};

export const isOptionsSelectionEmpty = (optionsSelection: OptionsSelection) => {
  return Object.values(optionsSelection).every(option => isObjectEmpty(option));
};

export const onEnter = (e: React.KeyboardEvent, cbFunc: (e: React.KeyboardEvent) => void) => {
  if (e.key === 'Enter') {
    cbFunc(e);
  }
};

export const getMEASProductTypeByConfigurationType = (configurationType: ConfigurationTypes | string) => {
  switch (configurationType) {
    case ConfigurationTypes.Jacket:
    case ConfigurationTypes.TuxedoJacket:
      return MeasProductTypes.Jacket;
    case ConfigurationTypes.Trousers:
    case ConfigurationTypes.TuxedoTrouser:
    case ConfigurationTypes.ExtraTrouser:
    case ConfigurationTypes.ExtraTuxedoTrouser:
      return MeasProductTypes.Trousers;
    case ConfigurationTypes.Waistcoat:
    case ConfigurationTypes.TuxedoWaistcoat:
      return MeasProductTypes.Waistcoat;
    case ConfigurationTypes.Shirt:
    case ConfigurationTypes.TuxedoShirt:
      return MeasProductTypes.Shirt;
    case ConfigurationTypes.Coat:
      return MeasProductTypes.OuterwearFormal;
    default:
      throw Error('No matching MEAS type for given CM configuration type');
  }
};

export const getConfigurationTypeIdByMeasurementsProductType = (product: ProductType, productType: ProductTypeMeas) => {
  switch (productType) {
    case 'Jacket':
      if (Products[product].includes(ConfigurationTypes.Jacket)) {
        return ConfigurationTypes.Jacket;
      } else if (Products[product].includes(ConfigurationTypes.TuxedoJacket)) {
        return ConfigurationTypes.TuxedoJacket;
      }
      break;
    case 'Trousers':
      if (Products[product].includes(ConfigurationTypes.Trousers)) {
        return ConfigurationTypes.Trousers;
      } else if (Products[product].includes(ConfigurationTypes.TuxedoTrouser)) {
        return ConfigurationTypes.TuxedoTrouser;
      }
      break;
    case 'Waistcoat':
      if (Products[product].includes(ConfigurationTypes.Waistcoat)) {
        return ConfigurationTypes.Waistcoat;
      } else if (Products[product].includes(ConfigurationTypes.TuxedoWaistcoat)) {
        return ConfigurationTypes.TuxedoWaistcoat;
      }
      break;
    case 'Shirt':
      if (Products[product].includes(ConfigurationTypes.Shirt)) {
        return ConfigurationTypes.Shirt;
      } else if (Products[product].includes(ConfigurationTypes.TuxedoShirt)) {
        return ConfigurationTypes.TuxedoShirt;
      }
      break;
    case MeasurementsCoatProductType:
      return ConfigurationTypes.Coat;

    default:
      throw Error('No matching configuration type ID for given product type');
  }
};

export const calculateFabricPrice = (product: ProductType, prices: Record<number, number>) => {
  let price = 0;
  if (prices) {
    Products[product].forEach(configurationType => {
      if (!isAdditonalProduct(product, configurationType)) {
        const configurationTypePrice = prices[configurationType];
        if (configurationTypePrice) {
          price += configurationTypePrice;
        }
      }
    });
  }

  return price;
};

export const getImageUrl = (imageUrlTemplate: string | null, imageName: string | null, fabricImageName?: string) => {
  if (!fabricImageName) {
    return {
      imageUrl: `${imageUrlTemplate}/${imageName}`,
      fallbackUrl: `${imageUrlTemplate}/${imageName}`,
    };
  }

  return {
    imageUrl: `${imageUrlTemplate?.replace('{fabricImageName}', fabricImageName)}/${imageName}`,
    fallbackUrl: `${imageUrlTemplate?.replace('{fabricImageName}', 'default')}/${imageName}`,
  };
};

export const getThumbnails = (
  imagePath: {
    imageUrl: string;
    fallbackUrl: string;
  },
  noTransformation?: boolean,
) => {
  const imageThumbnail = imageUrlBuilder({
    imageUrls: [imagePath.imageUrl],
    imageDefinition: imageDefinition.thumbnail,
    noTransformation,
  }).src;

  const fallbackImageThumbnail = imageUrlBuilder({
    imageUrls: [imagePath.fallbackUrl],
    imageDefinition: imageDefinition.thumbnail,
    noTransformation,
  }).src;

  return { imageThumbnail, fallbackImageThumbnail };
};

export const getImagePaths = (
  imageUrlTemplate: string | null,
  imageName: string,
  fabricImageName: string,
  noTransformation?: boolean,
) => {
  return getThumbnails(getImageUrl(imageUrlTemplate, imageName, fabricImageName), noTransformation);
};

export const getCookie = (cname: string) => {
  const name = cname + '=';
  const decodedCookie = decodeURIComponent(document.cookie);
  if (!decodedCookie) {
    return '';
  }

  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
};

export const checkConfigurationTypeRotationAvailable = (
  configurationType: ConfigurationTypes | undefined,
  isFetuareEnabled: boolean,
) => {
  return configurationType && isFetuareEnabled && rotationConfigurationTypes.includes(configurationType);
};

export const getConfigurationTypeNameByConfigurationType = (configurationType: ConfigurationTypes | string) => {
  switch (configurationType) {
    case ConfigurationTypes.Jacket:
      return 'CMTM Jacket';
    case ConfigurationTypes.Trousers:
    case ConfigurationTypes.ExtraTrouser:
      return 'CMTM Trousers';
    case ConfigurationTypes.Waistcoat:
      return 'CMTM Waistcoat';
    case ConfigurationTypes.Shirt:
      return 'CMTM Shirt';
    case ConfigurationTypes.Coat:
      return 'CMTM Coat';
    case ConfigurationTypes.TuxedoJacket:
      return 'CMTM Tuxedo Jacket';
    case ConfigurationTypes.TuxedoShirt:
      return 'CMTM Tuxedo Shirt';
    case ConfigurationTypes.TuxedoTrouser:
    case ConfigurationTypes.ExtraTuxedoTrouser:
      return 'CMTM Tuxedo Trouser';
    case ConfigurationTypes.TuxedoWaistcoat:
      return 'CMTM Tuxedo Waistcoat';
    default:
      return '';
  }
};

export const getProductIDByConfigurationType = (configurationType: ConfigurationTypes | string) => {
  const skuPrefix = 'CM';
  switch (configurationType) {
    case ConfigurationTypes.Jacket:
    case ConfigurationTypes.TuxedoJacket:
      return `${skuPrefix}-JACKET`;
    case ConfigurationTypes.Trousers:
    case ConfigurationTypes.TuxedoTrouser:
    case ConfigurationTypes.ExtraTrouser:
    case ConfigurationTypes.ExtraTuxedoTrouser:
      return `${skuPrefix}-TROUSERS`;
    case ConfigurationTypes.Waistcoat:
    case ConfigurationTypes.TuxedoWaistcoat:
      return `${skuPrefix}-WAISTCOAT`;
    case ConfigurationTypes.Shirt:
    case ConfigurationTypes.TuxedoShirt:
      return `${skuPrefix}-SHIRT`;
    case ConfigurationTypes.Coat:
      return `${skuPrefix}-COAT`;
    default:
      return '';
  }
};

// Avo helper function
const getUserId = (client: ClientType, accountNumber?: string) => {
  if (client === Clients.inStore) {
    return accountNumber ?? '';
  } else {
    return getCookie('uid') ?? '';
  }
};
// Avo helper function
const getLoggedInState = (client: ClientType, token?: string) => {
  if (client === Clients.inStore) {
    return token ? Avo.LoggedIn.TRUE : Avo.LoggedIn.FALSE;
  } else {
    return getCookie('isLoggedIn') ? Avo.LoggedIn.TRUE : Avo.LoggedIn.FALSE;
  }
};

export const getAvoEventParams = (state: State) => {
  return {
    eventLocation: `cm_${state.product!.toLowerCase()}_${state.section}` as EventLocationValueType,
    currency: state.selectedFabric?.prices?.code.toLowerCase() as CurrencyValueType,
    country: state.countryCode.toLowerCase() as CountryValueType,
    language: state.language as LanguageValueType,
    url: `https://suitsupply.com/custommade/${state.product!.toLowerCase()}/${state.section}`,
    locationId: `https://suitsupply.com/${state.language as LanguageValueType}-${
      state.countryCode.toLowerCase() as CountryValueType
    }/custommade/${state.product!.toLowerCase()}/${state.section}`,
    userId: getUserId(state.client, state.accountNr),
    loggedIn: getLoggedInState(state.client, state.token),
    pageType: Avo.PageType.CUSTOMMADE,
    eventLabel: '',
    eventCategory: Avo.EventCategory.CM_INTERACTIONS,
    gaClientId: localStorage.getItem('client_id') || '',
    gaSessionId: localStorage.getItem('session_id') || '',
    gaSessionNumber: localStorage.getItem('session_number') || '',
    fbFbp: getCookie('_fbp') || '',
    fbFbc: getCookie('_fbc') || '',
    gaAwClid: getCookie('_gcl_aw') || '',
  };
};

export const getAvoEventParamsForPageLoad = (state: State) => {
  return {
    ...getAvoEventParams(state),
    localeVisited: `${state.language as LanguageValueType}-${state.countryCode.toLowerCase() as CountryValueType}`,
    customDestinationPageName_: '',
  };
};

const getAvoCategories = (section: string) => {
  switch (section) {
    case Section.Trousers:
    case Section.TuxedoTrouser:
      return 'trousers';
    case Section.Waistcoat:
    case Section.TuxedoWaistcoat:
      return 'waistcoats';
    case Section.Shirt:
      return 'shirts';
    default:
      return 'jackets';
  }
};

export const getAvoEventParamsForProductViewed = (state: State, configurationType?: ConfigurationTypes) => {
  const price =
    state.selectedFabric?.prices?.configurationTypes[
      configurationType ?? getConfigurationTypeBySection(state.section ?? '') ?? 0
    ] ?? 0;

  const productId = getProductIDByConfigurationType(
    configurationType ?? getConfigurationTypeBySection(state.section ?? ''),
  ).toLowerCase();

  return {
    listId: 'custom-made',
    itemListName: 'custom made',
    country: state.countryCode.toLowerCase() as CountryValueType,
    language: state.language as LanguageValueType,
    productId: productId,
    category: getAvoCategories(
      configurationType ? getSectionByConfigurationType(configurationType) : (state.section ?? ''),
    ) as EventCategoryValueType,
    affiliation: `webstore-${
      state.selectedFabric?.prices?.code.toLowerCase() as CurrencyValueType
    }` as AffiliationValueType,
    name: productId,
    variant: null,
    price: price,
    quantity: 1,
    currency: state.selectedFabric?.prices?.code.toLowerCase() as CurrencyValueType,
    position: 1,
    value: price,
    sku: productId,
    locationId: `https://suitsupply.com/${state.language as LanguageValueType}-${
      state.countryCode.toLowerCase() as CountryValueType
    }/custommade/${state.product!.toLowerCase()}/${state.section}`,
    userId: getUserId(state.client, state.accountNr),
    loggedIn: getLoggedInState(state.client, state.token),
    pageType: Avo.PageType.CUSTOMMADE,
    gaClientId: localStorage.getItem('client_id') || '',
    gaSessionId: localStorage.getItem('session_id') || '',
    gaSessionNumber: localStorage.getItem('session_number') || '',
    fbFbp: getCookie('_fbp') || '',
    fbFbc: getCookie('_fbc') || '',
    gaAwClid: getCookie('_gcl_aw') || '',
    url: `https://suitsupply.com/custommade/${state.product!.toLowerCase()}/${state.section}`,
  };
};

export const getAvoEventParamsForProductAdded = (state: State, configurationType: ConfigurationTypes) => {
  return {
    ...getAvoEventParamsForProductViewed(state, configurationType),
    eventLocation: `cm_${state.product!.toLowerCase()}_${state.section}` as EventLocationValueType,
    eventLabel: '',
    eventCategory: Avo.EventCategory.CM_INTERACTIONS,
  };
};

export const getAvoEventParamsWithOption = (state: State, cmOption: string) => {
  return { ...getAvoEventParams(state), cmOption: cmOption.toLowerCase() };
};

export const capitalizeFirstLetter = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const clearStateLocalStorage = () => {
  localStorage.removeItem(stateStorageName);
  localStorage.removeItem(hiddenValuesStorageName);
  localStorage.removeItem(stateExpiryStorageName);
};

export const isAdditonalProduct = (product: ProductType, configurationTypeId: ConfigurationTypes) => {
  if (!product || !configurationTypeId) {
    return false;
  }
  return (additionalConfigurationTypeByProduct[product] as ConfigurationTypes[]).indexOf(configurationTypeId) > -1;
};

export const isExtraConfigurationInProduct = (product: ProductType, configurationTypeId: ConfigurationTypes) => {
  if (!product || !configurationTypeId) {
    return false;
  }
  return isAdditonalProduct(product, configurationTypeId) && +configurationTypeId >= 100;
};

export const isExtraConfigurationSection = (section: Section) => {
  const configurationType = getConfigurationTypeBySection(section);
  return configurationType && +configurationType >= 100;
};

export const convertInputToArray = (input: string | string[]) => {
  if (!input) {
    return [];
  }

  if (Array.isArray(input)) {
    return input;
  }

  if (input.includes(',')) {
    return input.split(',');
  }

  return [input];
};

export const shouldAddAdditonalProduct = (state: State) => {
  const affectedConfigurationTypeId = getConfigurationTypeBySection(state.section!);

  if (isExtraConfigurationInProduct(state.product!, affectedConfigurationTypeId)) return false;

  return (
    state.product &&
    isAdditonalProduct(state.product, affectedConfigurationTypeId) &&
    !state.isAdditionalProductAdded[affectedConfigurationTypeId]
  );
};

export const shouldShowRemoveAdditionalProduct = (state: State) => {
  const affectedConfigurationTypeId = getConfigurationTypeBySection(state.section!);

  if (isExtraConfigurationInProduct(state.product!, affectedConfigurationTypeId)) return false;

  return (
    state.product &&
    isAdditonalProduct(state.product, affectedConfigurationTypeId) &&
    state.isAdditionalProductAdded[affectedConfigurationTypeId]
  );
};

export const getConfigurationTypeText = (sectionConfigurationType: ConfigurationTypes) => {
  switch (sectionConfigurationType) {
    case ConfigurationTypes.Jacket:
    case ConfigurationTypes.TuxedoJacket:
      return 'Jacket';
    case ConfigurationTypes.Trousers:
    case ConfigurationTypes.TuxedoTrouser:
      return 'Trousers';
    case ConfigurationTypes.Waistcoat:
    case ConfigurationTypes.TuxedoWaistcoat:
      return 'Waistcoat';
    case ConfigurationTypes.Coat:
      return 'Coat';
    case ConfigurationTypes.Shirt:
    case ConfigurationTypes.TuxedoShirt:
      return 'Shirt';
    case ConfigurationTypes.ExtraTrouser:
    case ConfigurationTypes.ExtraTuxedoTrouser:
      return 'Extra Trousers';
    default:
      return '';
  }
};

export const getProductText = (productType: ProductType | undefined) => {
  if (!productType) {
    return '';
  }

  if (productType === 'Tuxedo') {
    return 'tuxedo suit';
  }

  return productType.replace('Tuxedo', 'tuxedo ').toLowerCase();
};

// Example: filterValues=season:Fall/Winter_quality:Wool+Cashmere_color:Grey
export const processPreFilterFormat = (prefilters: string): Record<string, string[]> => {
  let selectedFilters = {};
  prefilters.split('_')?.map(filterStr => {
    const [filterName, filterValues] = filterStr.split(':');
    const values = filterValues.split(',');
    selectedFilters = { ...selectedFilters, [filterName + 'Name']: values };
  });
  return selectedFilters;
};

export const validatePreFilters = (
  selectedFilters: Record<string, string[]>,
  fabricFilters: FabricFilter[],
): Record<string, string[]> => {
  let validatedPreFilters = {};
  Object.keys(selectedFilters).forEach(filterName => {
    const filter = fabricFilters.find(({ filterAttribute }) => filterName === filterAttribute);
    if (!filter) {
      return;
    }
    const filterValues = selectedFilters[filterName].filter(filterValue => {
      return filter.items.some(filterItem => {
        if (filterItem.subItems) {
          return filterItem.subItems.some(({ displayName }) => filterValue === displayName);
        } else {
          return filterValue === filterItem.displayName;
        }
      });
    });
    if (filterValues.length) {
      validatedPreFilters = {
        ...validatedPreFilters,
        [filterName]: filterValues,
      };
    }
  });

  return validatedPreFilters;
};

export const isLowStockFabric = (fabric: Fabric) => {
  return fabric.stockStatus === 'LOW';
};

export const findMatchingRTWFabric = (fabricList: typeFabricList, rtwProductCodes: string[], fabricCode: string) => {
  let matchingFabric: Fabric | undefined;

  if (fabricCode) {
    matchingFabric = Object.values(fabricList).find(fabric => fabric.code === fabricCode);
  }

  if (rtwProductCodes.length > 0 && !matchingFabric) {
    matchingFabric = Object.values(fabricList).find(
      fabric => rtwProductCodes.some(code => fabric.rtwProductCodes.includes(code)) && !isLowStockFabric(fabric),
    );
  }

  return matchingFabric;
};

export const filterLowStockFabricList = (ids: string[], fabricList: typeFabricList) => {
  return ids.filter((id: string) => !isLowStockFabric(fabricList[id]));
};

export const getProductDefaultHiddenItemsState = (product: ProductType) => {
  const defaultHiddenState: HiddenOptionsAndValues = {
    optionsPerConfiguration: {},
    optionValuesPerConfiguration: {},
    monogramColors: [],
    monogramPositions: [],
  };

  Products[product].forEach(configurationType => {
    defaultHiddenState.optionsPerConfiguration[configurationType] = [];
    defaultHiddenState.optionValuesPerConfiguration[configurationType] = [];
  });

  return defaultHiddenState;
};

export const initialiseConfigurationBasedOptionsSelection = (product: ProductType) => {
  const configurationBasedOptionsSelection: OptionsSelection = {};

  Products[product].forEach(configurationType => {
    configurationBasedOptionsSelection[configurationType] = {};
  });

  return configurationBasedOptionsSelection;
};

export const getSingleOptions = (optionItem: Option[]) => {
  let items: Option[] = [];
  optionItem.forEach(option => {
    if (option.options) {
      option.options.forEach(subOption => {
        let name = subOption.name;
        if (option.name !== subOption.name) {
          name = `${option.name} ${subOption.name}`;
        }

        items.push({ ...subOption, name: name });
      });
    } else {
      items.push(option);
    }
  });
  return items;
};

// Replacing placeholder values in template indicated with {numeric_value}
// e.g. formatString('template example {0} with {1} values', one, two) returns => 'template example one with two values'
export const formatString = (template: string, ...values: (string | number)[]): string => {
  return template.replace(/{(\d+)}/g, (match, number) => {
    return typeof values[number] !== 'undefined' ? String(values[number]) : match;
  });
};

export const findFabricAndFabricGroupDefaultSelection = (
  fabricList: typeFabricList,
  fabricGroups: FabricGroup[],
  defaultFabricId: string,
  fabricToPreselect?: Fabric,
) => {
  const filteredLowStockFabricList = filterLowStockFabricList(fabricGroups[0]?.fabrics.map(String), fabricList);
  const defaultFabric = fabricList[defaultFabricId];
  let fabricToSelect = fabricToPreselect;
  if (!fabricToSelect) {
    fabricToSelect = defaultFabric ?? fabricList[filteredLowStockFabricList[0]];
  }

  const fabricGroupToSelect = fabricGroups.find(group => group.fabrics.includes(fabricToSelect.id)) ?? fabricGroups[0];

  return { fabricToSelect, fabricGroupToSelect };
};

export const getConfiguratorOrigin = (
  origin: ConfiguratorOrigin,
  preloadConfiguration: string,
  productCodes: string,
): ConfiguratorOrigin => {
  if (!isFalsy(preloadConfiguration)) {
    return ConfiguratorOrigin.Clone;
  }

  if (productCodes) {
    return ConfiguratorOrigin.CustomizeFlow;
  }

  return origin;
};

export const stepIncrement = 9;

export enum rotationPosition {
  front = 0,
  side = 1,
  back = 2,
}

export const rotationConfig = {
  front: {
    imageSuffix: 'R00',
    spriteStep: 0,
  },
  side: {
    imageSuffix: 'R01',
    spriteStep: 1 * stepIncrement,
  },
  back: {
    imageSuffix: 'R02',
    spriteStep: 2 * stepIncrement,
  },
};

export const isBrowserRender = () => typeof window !== 'undefined';

export const isServerRender = () => !isBrowserRender();

export const checkRotationStep = (option: Option | undefined) => {
  if (!option) {
    return rotationConfig.front;
  }

  switch (option.rotationPosition) {
    case rotationPosition.side:
      return rotationConfig.side;
    case rotationPosition.back:
      return rotationConfig.back;
    default:
      return rotationConfig.front;
  }
};

export const isProductBaseStyleActive = (
  selectedBaseStyle: Record<string, StylePreset>,
  section: string | null,
  configurationType?: string,
) => {
  let configurationTypeId = '';

  if (section && section !== Section.Fabric && section !== Section.Summary)
    configurationTypeId = getConfigurationTypeBySection(section ?? '');

  if (configurationType)
    configurationTypeId = getConfigurationTypeBySection(getSectionByConfigurationType(configurationType ?? ''));

  return !!selectedBaseStyle[configurationTypeId];
};

export const logConsole = (message: string, params: any, isLogEnabled: boolean) => {
  if (isLogEnabled && isBrowserRender()) {
    console.log(message, params);
  }
};

export const getDatadogErrorObject = (error: Error) => {
  // custom error object is needed to be able separate errors in datadog original errors and centralize our errors
  return {customError: {errorStatusText: (error as Error).message, stack: (error as Error).stack}};
}
